import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone, signal } from '@angular/core';
import { catchError, of } from 'rxjs';

export interface LanguageFile {
  name: string;
  nativeName: string;
  translations: Record<string, string>;
}

type TextState = { source: string; rendered: string };

@Injectable({ providedIn: 'root' })
export class LanguageService {
  readonly languages = signal<LanguageFile[]>([]);
  readonly currentLanguage = signal('fr');
  readonly translations = signal<Record<string, string>>({});

  private observer?: MutationObserver;
  private readonly textStates = new WeakMap<Text, TextState>();
  private readonly attributeStates = new WeakMap<Element, Map<string, TextState>>();
  private readonly translatedAttributes = ['aria-label', 'title', 'placeholder', 'alt'];
  private messageTemplates: Array<{ pattern: RegExp; tokens: string[]; target: string }> = [];
  private requestId = 0;
  private translationScheduled = false;

  constructor(private readonly http: HttpClient, private readonly zone: NgZone) {
    const saved = localStorage.getItem('index-liv-language');
    this.http.get<Record<string, LanguageFile>>('/i18n/languages.json').pipe(
      catchError(() => of({ fr: { name: 'French', nativeName: 'Français', translations: {} } } as Record<string, LanguageFile>))
    ).subscribe(files => {
      this.languages.set(Object.entries(files).map(([code, file]) => ({ ...file, name: code })));
      this.loadLanguage(saved && files[saved] ? saved : 'fr');
    });
  }

  startDomTranslation(): void {
    if (this.observer) return;
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => {
        if (this.translationScheduled) return;
        this.translationScheduled = true;
        requestAnimationFrame(() => {
          this.translationScheduled = false;
          this.translateDom();
        });
      });
      this.observer.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: this.translatedAttributes });
      this.translateDom();
    });
  }

  setLanguage(code: string): void {
    if (!this.languages().some(language => language.name === code)) return;
    localStorage.setItem('index-liv-language', code);
    this.loadLanguage(code);
  }

  translate(source: string): string {
    const dictionary = this.translations();
    if (dictionary[source] !== undefined) return dictionary[source];
    for (const template of this.messageTemplates) {
      const match = template.pattern.exec(source);
      if (!match) continue;
      let rendered = template.target;
      template.tokens.forEach((token, index) => {
        const value = token === 'phase' || token === 'status'
          ? this.translate(match[index + 1])
          : match[index + 1];
        rendered = rendered.replaceAll(`{${token}}`, value);
      });
      return rendered;
    }
    const counted = /^(\d+)(\s+)(.+)$/.exec(source);
    if (counted && dictionary[counted[3]] !== undefined) {
      return counted[1] + counted[2] + dictionary[counted[3]];
    }
    return source;
  }

  private loadLanguage(code: string): void {
    const requestId = ++this.requestId;
    this.http.get<LanguageFile>(`/i18n/${code}.json`).pipe(
      catchError(() => of({ name: code, nativeName: code, translations: {} }))
    ).subscribe(file => {
      if (requestId !== this.requestId) return;
      this.currentLanguage.set(code);
      this.translations.set(file.translations ?? {});
      this.messageTemplates = Object.entries(file.translations ?? {})
        .filter(([key]) => /\{[a-zA-Z]+\}/.test(key))
        .sort(([a], [b]) => b.length - a.length)
        .map(([key, target]) => {
          const tokens: string[] = [];
          let pattern = '^';
          let cursor = 0;
          for (const match of key.matchAll(/\{([a-zA-Z]+)\}/g)) {
            pattern += this.escapePattern(key.slice(cursor, match.index));
            pattern += match[1] === 'n' ? '(\\d+)' : '(.+?)';
            tokens.push(match[1]);
            cursor = match.index! + match[0].length;
          }
          pattern += this.escapePattern(key.slice(cursor)) + '$';
          return { pattern: new RegExp(pattern), tokens, target };
        });
      document.documentElement.lang = code;
      document.title = this.translate('Index des livrables');
      this.translateDom();
    });
  }

  private translateDom(): void {
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const textNode = node as Text;
      if (textNode.parentElement?.closest('script, style, textarea, input')) continue;
      const current = textNode.textContent ?? '';
      const old = this.textStates.get(textNode);
      const source = old && old.rendered === current ? old.source : current;
      const rendered = this.translateText(source);
      this.textStates.set(textNode, { source, rendered });
      if (current !== rendered) textNode.textContent = rendered;
    }

    for (const element of document.body.querySelectorAll('*')) {
      for (const attribute of this.translatedAttributes) {
        const current = element.getAttribute(attribute);
        if (current === null) continue;
        let states = this.attributeStates.get(element);
        if (!states) {
          states = new Map();
          this.attributeStates.set(element, states);
        }
        const old = states.get(attribute);
        const source = old && old.rendered === current ? old.source : current;
        const rendered = this.translateText(source);
        states.set(attribute, { source, rendered });
        if (current !== rendered) element.setAttribute(attribute, rendered);
      }
    }
  }

  private translateText(source: string): string {
    const match = /^(\s*)([\s\S]*?)(\s*)$/.exec(source);
    if (!match) return source;
    const key = match[2].replace(/\u00a0/g, ' ').replace(/\s+/g, ' ');
    return match[1] + this.translate(key) + match[3];
  }

  private escapePattern(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
